import { useState, type ReactNode } from 'react'
import type {
  VspBenefits,
  VspDetailedBenefits,
  VspBenefitLine,
  VspPrescriptionLenses,
  VspHtmlSection,
  VspSectionsBenefits,
} from '../types'
import { isVspDetailedBenefits, isVspSectionsBenefits, prescriptionLensesHaveData, vspBenefitLineHasData } from '../types'
import { benefitHtmlHasText } from '../../shared/benefitHtml'
import Accordion from './Accordion'
import SmsRecoverySection from './SmsRecoverySection'
import VisionSlideoutFrame, { type VisionCheckSummary } from './VisionSlideoutFrame'
import CheckIdentityEditor from './CheckIdentityEditor'
import { UNSAVED_WARNING } from '../utils/unsavedWork'
import { useUnsavedGuard } from '../utils/useUnsavedGuard'
import { canCorrect } from '../utils/checkCorrection'
import type { EligibilityCheckDoc } from '../types'
import VspPlanTableModal from './VspPlanTableModal'
import { useBrowserBackSentinel } from '../utils/useBrowserBackSentinel'

interface VspSlideoutProps {
  /** Section/HTML (2026-07 VSP bot), structured (post-2026 bot) or flat
   *  (legacy) benefits — the slideout renders whichever shape it receives,
   *  so old stored checks still open. */
  vspBenefits: VspBenefits | VspDetailedBenefits | VspSectionsBenefits
  summary: VisionCheckSummary
  onClose: () => void
  /** True when the displayed stored check is in the SMS recovery loop. */
  inRecoveryLoop?: boolean
  /** True when the stored check is SMS-eligible (recoveryAction
   *  patient_correction) — shows the SMS recovery section even before the
   *  outreach sweep claims the check (link disabled until it does). */
  smsRecoveryEligible?: boolean
  /** Jump to this check's Recovery Loop case (the SMS conversation). */
  onViewRecoveryConversation?: () => void
  /** #314 — in-place correction. The vision panels had no correction path at
   *  all, so a wrong member id or payer on an RPA check meant starting over
   *  somewhere else. Absent → the editor is not offered. */
  orgId?: string
  check?: EligibilityCheckDoc | null
  onCorrected?: () => void
  /** Refresh hook fired the moment a correction runs, before any clinic-mapping
   *  offer is answered — so closing the panel cannot leave a stale view. */
  onCorrectionFired?: () => void
  /** False during a read-only (view-mode) staff session. Without it the fields
   *  and Save are live and the handler's `requireWrite` refuses only AFTER
   *  someone has typed into PHI fields. */
  canWrite?: boolean
  /** Unsaved-edit signal for the DASHBOARD's browser-Back sentinel (#274). The
   *  panel's own guard covers Escape / the overlay / the X, but Back never
   *  reaches a panel handler — without this channel `detailDirty` had exactly
   *  one producer (the failure slideout) and a typed correction here was
   *  discarded by Back with no warning at all. */
  onDirtyChange?: (dirty: boolean) => void
  /** The parent refused a close on this panel's behalf — browser Back, which
   *  the panel can't see. Renders the same inline bar so the refused press has
   *  a visible reason instead of being a dead press (#274, #231). */
  warnedByParent?: boolean
}

function formatCurrency(value: number | null): string {
  if (value === null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)
}

// ── Section icons ─────────────────────────────────────────────────────────

const EyeIcon = (
  <svg className="financial-summary-section__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

const GlassesIcon = (
  <svg className="financial-summary-section__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="15" r="4" />
    <circle cx="18" cy="15" r="4" />
    <path d="M10 15a2 2 0 0 1 4 0M2 11l2-5h3M22 11l-2-5h-3" />
  </svg>
)

const StarIcon = (
  <svg className="financial-summary-section__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
)

// Shared empty state — a completed check whose stored benefits carry nothing
// displayable (upstream portal change ate the data).
function NoBenefitsData() {
  return (
    <div className="slideout-panel__no-data">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <p className="slideout-panel__no-data-title">No Benefits Data Returned</p>
      <p className="slideout-panel__no-data-text">
        The VSP check completed but returned no benefit details. This usually points to an upstream portal change.
      </p>
    </div>
  )
}

// ── Section/HTML format (2026-07 bot) ─────────────────────────────────────

// The bot delivers each portal section as a plan name + sanitized HTML table.
// List the plan names here; the table itself is far wider than the slideout,
// so each plan opens in the wide VspPlanTableModal popup.
function SectionsVspView({ benefits }: { benefits: VspSectionsBenefits }) {
  const [openSection, setOpenSection] = useState<VspHtmlSection | null>(null)
  // The plan-table popup is its own nested level on top of the VSP slideout
  // (#231) — same wound as the medical Benefits Explorer: without this,
  // browser Back while the popup is open falls through to the slideout's own
  // sentinel and closes the whole check instead of just the popup.
  useBrowserBackSentinel(openSection !== null, () => setOpenSection(null))
  // Defensive filter matching the ingest rule: only sections whose table has
  // visible text are displayable — a label alone is not benefits data. The
  // webhook parser already enforces this, but a malformed (or pre-rule) doc
  // degrades to the empty state instead of listing plans with empty popups.
  const sections = (benefits.sections ?? []).filter(s => benefitHtmlHasText(s.html ?? ''))

  if (sections.length === 0) return <NoBenefitsData />

  return (
    <div className="financial-summary-section">
      <div className="financial-summary-section__header">
        {EyeIcon}
        <span>PLANS</span>
      </div>
      <div className="vsp-plan-list">
        {sections.map((section, i) => (
          <button
            type="button"
            className="vsp-plan-item"
            key={`${section.label}-${i}`}
            onClick={() => setOpenSection(section)}
          >
            <span className="vsp-plan-item__name">{section.label || `Plan ${i + 1}`}</span>
          </button>
        ))}
      </div>
      {openSection && (
        <VspPlanTableModal
          label={openSection.label || 'Plan benefits'}
          html={openSection.html}
          onClose={() => setOpenSection(null)}
        />
      )}
    </div>
  )
}

// ── Detailed (structured) format ──────────────────────────────────────────

function BenefitSection({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <div className="financial-summary-section">
      <div className="financial-summary-section__header">
        {icon}
        <span>{title}</span>
      </div>
      <div className="vsp-benefit-cards">{children}</div>
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  if (!value.trim()) return null
  return (
    <div className="vsp-detail-row">
      <span className="vsp-detail-row__label">{label}</span>
      <span className="vsp-detail-row__value">{value}</span>
    </div>
  )
}

// Glance value for the collapsed header: prefer the copay (what the desk
// collects), else the coverage text (e.g. "Covered in full", "$150 allowance").
// Returns null when the benefit carries neither, so no chip renders.
function benefitSummary(b: { copay: string; coverage: string }): { value: string; label?: string } | null {
  const copay = b.copay.trim()
  if (copay) return { value: copay, label: 'Copay' }
  const coverage = b.coverage.trim()
  if (coverage) return { value: coverage }
  return null
}

// The right-aligned glance chip in the collapsed header (hidden once expanded,
// since the body repeats Coverage/Copay).
function SummaryChip({ summary }: { summary: { value: string; label?: string } }) {
  return (
    <span className="accordion__summary">
      {summary.label && <span className="accordion__summary-label">{summary.label}</span>}
      {summary.value}
    </span>
  )
}

// One benefit rendered as a collapsible item (shared Accordion). Collapsed by
// default; the header carries the benefit name plus the glance chip.
function BenefitCard({ benefit }: { benefit: VspBenefitLine }) {
  const summary = benefitSummary(benefit)
  return (
    <Accordion
      title={benefit.name || '—'}
      aside={summary ? <SummaryChip summary={summary} /> : undefined}
      hideAsideWhenOpen
    >
      <div className="vsp-detail-rows">
        <DetailRow label="Coverage" value={benefit.coverage} />
        <DetailRow label="Copay" value={benefit.copay} />
        <DetailRow label="Frequency" value={benefit.frequency} />
      </div>
      {benefit.description.trim() && (
        <p className="vsp-benefit-card__desc">{benefit.description}</p>
      )}
      {benefit.extraSavings?.trim() && (
        <div className="vsp-benefit-card__savings">
          <span className="vsp-benefit-card__savings-label">Extra savings</span>
          <span>{benefit.extraSavings}</span>
        </div>
      )}
    </Accordion>
  )
}

function PrescriptionLensesCard({ lenses }: { lenses: VspPrescriptionLenses }) {
  const summary = benefitSummary(lenses)
  return (
    <Accordion
      title="Prescription Lenses"
      aside={summary ? <SummaryChip summary={summary} /> : undefined}
      hideAsideWhenOpen
    >
      <div className="vsp-detail-rows">
        <DetailRow label="Coverage" value={lenses.coverage} />
        <DetailRow label="Copay" value={lenses.copay} />
        <DetailRow label="Frequency" value={lenses.frequency} />
      </div>
      {lenses.fullyCoveredLensEnhancements.trim() && (
        <div className="vsp-lens-block">
          <div className="vsp-lens-block__title">Fully Covered Lens Enhancements</div>
          <p className="vsp-lens-block__text">{lenses.fullyCoveredLensEnhancements}</p>
        </div>
      )}
      {lenses.lensEnhancementsCoveredWithAdditionalCopay.length > 0 && (
        <div className="vsp-lens-block">
          <div className="vsp-lens-block__title">Lens Enhancements — Additional Copay</div>
          <div className="vsp-lens-enhancements">
            {lenses.lensEnhancementsCoveredWithAdditionalCopay.map((enhancement, i) => (
              <div className="vsp-detail-row" key={`${enhancement.name}-${i}`}>
                <span className="vsp-detail-row__label">{enhancement.name || '—'}</span>
                <span className="vsp-detail-row__value">{enhancement.value || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Accordion>
  )
}

function DetailedVspSections({ benefits }: { benefits: VspDetailedBenefits }) {
  // Defensive access: `benefits` is a typed-but-unvalidated Firestore read and
  // `isVspDetailedBenefits` only checks for the `vspWellVisionPlan` key. The
  // backend parser always writes a complete shape, but a malformed/empty
  // structured doc degrades to the empty-state below instead of throwing.
  const wellVision = benefits.vspWellVisionPlan
  const lenses = wellVision?.prescriptionLenses
  // Drop blank placeholder lines (an empty portal row parses to all-empty
  // strings) so they don't render as "—" accordions and an all-blank section
  // is hidden entirely rather than shown as an empty header.
  const wellVisionLines = (wellVision?.benefits ?? []).filter(vspBenefitLineHasData)
  const specialtyLines = (benefits.specialtyEyecareServices?.benefits ?? []).filter(vspBenefitLineHasData)
  const hasLenses = lenses ? prescriptionLensesHaveData(lenses) : false
  const hasWellVision = wellVisionLines.length > 0 || hasLenses
  const hasSpecialty = specialtyLines.length > 0

  if (!hasWellVision && !hasSpecialty) {
    return <NoBenefitsData />
  }

  return (
    <>
      {hasWellVision && (
        <BenefitSection title="WELLVISION PLAN" icon={EyeIcon}>
          {wellVisionLines.map((benefit, i) => (
            <BenefitCard benefit={benefit} key={`${benefit.name}-${i}`} />
          ))}
          {hasLenses && lenses && <PrescriptionLensesCard lenses={lenses} />}
        </BenefitSection>
      )}

      {hasSpecialty && (
        <BenefitSection title="SPECIALTY EYECARE SERVICES" icon={StarIcon}>
          {specialtyLines.map((benefit, i) => (
            <BenefitCard benefit={benefit} key={`${benefit.name}-${i}`} />
          ))}
        </BenefitSection>
      )}
    </>
  )
}

// ── Legacy (flat) format ──────────────────────────────────────────────────

function FlatVspSections({ benefits }: { benefits: VspBenefits }) {
  return (
    <>
      {/* Exams section */}
      <div className="financial-summary-section">
        <div className="financial-summary-section__header">
          {EyeIcon}
          <span>EXAMS</span>
        </div>
        <div className="simple-value-cards">
          <div className="simple-value-card">
            <div className="simple-value-card__title">Routine Exam Copay</div>
            <div className="simple-value-card__row">
              <span className="simple-value-card__value">{formatCurrency(benefits.routineExamCopay)}</span>
            </div>
          </div>
          <div className="simple-value-card">
            <div className="simple-value-card__title">Medical Exam Copay</div>
            <div className="simple-value-card__row">
              <span className="simple-value-card__value">{formatCurrency(benefits.medicalExamCopay)}</span>
            </div>
          </div>
          <div className="simple-value-card">
            <div className="simple-value-card__title">General Copay</div>
            <div className="simple-value-card__row">
              <span className="simple-value-card__value">{formatCurrency(benefits.genCopay)}</span>
            </div>
          </div>
          <div className="simple-value-card">
            <div className="simple-value-card__title">Deductible</div>
            <div className="simple-value-card__row">
              <span className="simple-value-card__value">{formatCurrency(benefits.deductible)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Materials section */}
      <div className="financial-summary-section">
        <div className="financial-summary-section__header">
          {GlassesIcon}
          <span>MATERIALS</span>
        </div>
        <div className="simple-value-cards">
          <div className="simple-value-card">
            <div className="simple-value-card__title">Materials Copay</div>
            <div className="simple-value-card__row">
              <span className="simple-value-card__value">{formatCurrency(benefits.materialCopay)}</span>
            </div>
          </div>
          <div className="simple-value-card">
            <div className="simple-value-card__title">Max Frame Allowance</div>
            <div className="simple-value-card__row">
              <span className="simple-value-card__value">{formatCurrency(benefits.maxFrameAllowance)}</span>
            </div>
          </div>
          <div className="simple-value-card">
            <div className="simple-value-card__title">Contact Lens Allowance</div>
            <div className="simple-value-card__row">
              <span className="simple-value-card__value">{formatCurrency(benefits.contactLensAllowance)}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default function VspSlideout({
  vspBenefits,
  summary,
  onClose,
  inRecoveryLoop,
  smsRecoveryEligible,
  onViewRecoveryConversation,
  orgId,
  check,
  onCorrected,
  onCorrectionFired,
  canWrite = true,
  onDirtyChange,
  warnedByParent = false,
}: VspSlideoutProps) {
  // #274 — one component, four surfaces, one behaviour: closing this panel
  // mid-correction warns before it discards.
  const { guardedClose, setDirty, showWarning, dirty } = useUnsavedGuard(onClose)
  return (
    <VisionSlideoutFrame
      subtitle="VSP eligibility overview"
      ariaLabel="Vision Benefits"
      summary={summary}
      onClose={guardedClose}
    >
      {isVspDetailedBenefits(vspBenefits) ? (
        <DetailedVspSections benefits={vspBenefits} />
      ) : isVspSectionsBenefits(vspBenefits) ? (
        <SectionsVspView benefits={vspBenefits} />
      ) : (
        <FlatVspSections benefits={vspBenefits} />
      )}

      <SmsRecoverySection
        inRecoveryLoop={inRecoveryLoop}
        smsRecoveryEligible={smsRecoveryEligible}
        onView={onViewRecoveryConversation}
      />
      {orgId && check && onCorrected && canCorrect(check) && (
        <div className="failure-slideout__edit">
          <CheckIdentityEditor
            canWrite={canWrite}
            onFired={onCorrectionFired}
            orgId={orgId}
            check={check}
            onDirtyChange={d => { setDirty(d); onDirtyChange?.(d) }}
            onCorrected={onCorrected}
          />
        </div>
      )}
          {(showWarning || (warnedByParent && dirty)) && (
            <div className="slideout-unsaved" role="status">
              <span>{UNSAVED_WARNING}</span>
              <button className="slideout-unsaved__discard" onClick={onClose}>
                Discard
              </button>
            </div>
          )}

    </VisionSlideoutFrame>
  )
}
